"use client";

import {getLocalTimeZone, parseDate, today} from "@internationalized/date";
import {DateRangePicker} from "@nextui-org/react";
import {Label} from "flowbite-react";
import React, {useEffect, useRef, useState} from "react";
import DatePicker, {
  CalendarProps,
  DateObject,
  DatePickerProps
} from "react-multi-date-picker";
import Icon from "react-multi-date-picker/components/icon";
import DatePanel from "react-multi-date-picker/plugins/date_panel";
import TimePicker from "react-multi-date-picker/plugins/time_picker";
import {NeoButton} from "../neo-button/neo-button";
interface NeoDatePickerProps
  extends Omit<CalendarProps, "onChange">,
    Omit<DatePickerProps, "onChange"> {
  type?: "input" | "icon" | "range" | undefined;
  label?: any;
  errorMessage?: any;
  className?: string;
  labelClassName?: string;
  inputClassName?: string;
  showTime?: boolean;
  rangedefaultValue?: any;
  handleDateChange?: (date: DateRange | Date | Date[] | null) => void;
  allowFuture?: boolean;
  openCalenManually?: any;
}
export const NeoDatePicker: React.FC<NeoDatePickerProps> = ({
  type = "input",
  label,
  errorMessage,
  className,
  labelClassName,
  inputClassName,
  showTime,
  handleDateChange,
  rangedefaultValue,
  allowFuture = false,
  openCalenManually,
  ...props
}) => {
  const datePickerRef = useRef<any>();
  const [inputErrorMessage, setInputErrorMessage] = useState<string | null>(
    null
  );
  const allowedDateInputPattern = showTime ? /^[0-9\-:\s]*$/ : /^[0-9-]*$/;
  const allowedDateKeyPattern = showTime ? /^[0-9\-:\s]$/ : /^[0-9-]$/;
  const allowedControlKeys = new Set([
    "Backspace",
    "Delete",
    "Tab",
    "Enter",
    "Escape",
    "ArrowLeft",
    "ArrowRight",
    "ArrowUp",
    "ArrowDown",
    "Home",
    "End"
  ]);
  const setDefaultDateTimeRange = () => {
    const now = new Date();
    const defaultFrom = new DateObject({date: now}).set({
      hour: 0,
      minute: 0,
      second: 0
    });

    const defaultTo = new DateObject({date: now}).set({
      hour: 23,
      minute: 59,
      second: 59
    });

    if (
      !rangedefaultValue ||
      !rangedefaultValue.from ||
      !rangedefaultValue.to
    ) {
      return [defaultFrom, defaultTo];
    }

    const from = new DateObject(rangedefaultValue.from);
    const to = new DateObject(rangedefaultValue.to);

    return [from, to];
  };
  const [selectedRangeDates, setSelectedRangeDates] = useState<DateObject[]>(
    () => {
      return setDefaultDateTimeRange();
    }
  );
  useEffect(() => {
    if (type === "icon") {
      if (selectedRangeDates && selectedRangeDates.length === 2) {
        const from = selectedRangeDates[0]?.toDate();
        const to = selectedRangeDates[1]?.toDate();
        if (from && to) {
          //from.setHours(0, 0, 0, 0);
          const result: DateRange = {from, to};
          handleDateChange?.(result);
          // setSelectedRangeDates(e);
          // setSelectedRangeDates([from, to]);
        }
      }
    }
  }, [handleDateChange, selectedRangeDates, type]);

  const onDateChange = (e: DateObject | DateObject[] | null) => {
    setInputErrorMessage(null);
    if (!e) {
      handleDateChange?.(null);
      return;
    }

    if (e instanceof DateObject) {
      handleDateChange?.(e?.toDate());
    } else if (Array.isArray(e) && e.length > 0 && e[0] instanceof DateObject) {
      if (props.range) {
        const from = e[0]?.toDate();
        const to = e[1]?.toDate();
        if (from && to) {
          //from.setHours(0, 0, 0, 0);
          const result: DateRange = {from, to};
          //handleDateChange?.(result);
          setSelectedRangeDates(e);
          // setSelectedRangeDates([from, to]);
        }
      } else {
        const result = e.map(x => x?.toDate());
        handleDateChange?.(result);
      }
    }
  };
  useEffect(() => {
    if (typeof openCalenManually === "function") {
      openCalenManually(datePickerRef);
    }
  }, [openCalenManually]);
  const handleApplyClick = () => {
    datePickerRef?.current?.closeCalendar();
  };

  const isCompleteYear = (year: any) =>
    typeof year === "number" && String(Math.abs(year)).length === 4;

  const onRangeDateChange = (event: any) => {
    if (
      event?.start &&
      event?.end &&
      isCompleteYear(event.start.year) &&
      isCompleteYear(event.end.year)
    ) {
      const startDate = new Date(
        event.start.year,
        event.start.month - 1,
        event.start.day
      );
      const endDate = new Date(
        event.end.year,
        event.end.month - 1,
        event.end.day
      );
      handleDateChange?.({from: startDate, to: endDate});
    }
    // Ignore incomplete edits (e.g. year segment mid-typing with < 4 digits)
    // so we never propagate a Date that can't round-trip through ISO parsing.
  };

  const toIsoDateString = (date: Date) => {
    const year = date?.getFullYear();
    if (!date || isNaN(date.getTime()) || !isCompleteYear(year)) {
      date = new Date();
    }
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
  };

  const extractDateObjtostr = () => {
    const from = rangedefaultValue?.from ?? new Date();
    const to = rangedefaultValue?.to ?? new Date();

    return {
      start: parseDate(toIsoDateString(from)),
      end: parseDate(toIsoDateString(to))
    };
  };
  const getDateLimits = (isFuture = false) => {
    const tz = getLocalTimeZone();

    return {
      minValue: isFuture ? today(tz) : undefined, // restrict past when future allowed
      maxValue: isFuture ? undefined : today(tz) // restrict future when false
    };
  };
  const {minValue, maxValue} = getDateLimits(allowFuture);
  return (
    <>
      {(() => {
        switch (type) {
          case "input": {
            return (
              <div className={`grid w-full grid-cols-1 ${className ?? ""}`}>
                {label && (
                  <Label
                    className={`mb-1 block text-base font-normal ${errorMessage ? "text-red-500" : ""} ${labelClassName ?? ""}`}
                    value={label}
                  />
                )}
                <DatePicker
                  {...props}
                  format={showTime ? "YYYY-MM-DD HH:mm:ss" : "YYYY-MM-DD"}
                  onChange={onDateChange}
                  containerClassName="w-full [&_svg]:!right-3.5 "
                  placeholder={props.placeholder ?? "Please select date"}
                  render={(value, openCalendar, handleValueChange) => (
                    <div className="relative w-full">
                      <input
                        type="text"
                        value={value ?? ""}
                        onChange={event => {
                          if (
                            !allowedDateInputPattern.test(event.target.value)
                          ) {
                            setInputErrorMessage("Only numbers are allowed.");
                            return;
                          }

                          setInputErrorMessage(null);
                          handleValueChange(event);
                        }}
                        onKeyDown={event => {
                          if (
                            !allowedControlKeys.has(event.key) &&
                            !allowedDateKeyPattern.test(event.key)
                          ) {
                            event.preventDefault();
                            setInputErrorMessage("Only numbers are allowed.");
                            return;
                          }

                          setInputErrorMessage(null);
                        }}
                        onPaste={event => {
                          const pastedValue =
                            event.clipboardData.getData("text");
                          if (!allowedDateInputPattern.test(pastedValue)) {
                            event.preventDefault();
                            setInputErrorMessage("Only numbers are allowed.");
                            return;
                          }

                          setInputErrorMessage(null);
                        }}
                        onFocus={openCalendar}
                        placeholder={props.placeholder ?? "Please select date"}
                        autoComplete="off"
                        inputMode="numeric"
                        className={`border-neo-gray-100 h-11 min-h-11 w-full rounded-medium border-2 bg-neo-white px-3 pr-11 shadow-none outline-none hover:border-neo-gray ${errorMessage && "!border-red-500"} ${inputClassName ?? ""}`}
                      />
                      <div
                        className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer"
                        onMouseDown={event => event.preventDefault()}
                        onClick={openCalendar}
                      >
                        <svg
                          width="15"
                          height="16"
                          viewBox="0 0 21 22"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M18.5725 1.68841H16.0399V0.844207C16.0399 0.620309 15.951 0.405582 15.7927 0.247262C15.6343 0.0889429 15.4196 0 15.1957 0C14.9718 0 14.7571 0.0889429 14.5988 0.247262C14.4405 0.405582 14.3515 0.620309 14.3515 0.844207V1.68841H5.90945V0.844207C5.90945 0.620309 5.8205 0.405582 5.66219 0.247262C5.50387 0.0889429 5.28914 0 5.06524 0C4.84134 0 4.62662 0.0889429 4.4683 0.247262C4.30998 0.405582 4.22103 0.620309 4.22103 0.844207V1.68841H1.68841C1.24062 1.68841 0.811164 1.8663 0.494525 2.18294C0.177886 2.49958 0 2.92903 0 3.37683V20.261C0 20.7088 0.177886 21.1382 0.494525 21.4549C0.811164 21.7715 1.24062 21.9494 1.68841 21.9494H18.5725C19.0203 21.9494 19.4498 21.7715 19.7664 21.4549C20.0831 21.1382 20.261 20.7088 20.261 20.261V3.37683C20.261 2.92903 20.0831 2.49958 19.7664 2.18294C19.4498 1.8663 19.0203 1.68841 18.5725 1.68841ZM5.48734 17.7283C5.23689 17.7283 4.99206 17.6541 4.78382 17.5149C4.57558 17.3758 4.41327 17.178 4.31743 16.9466C4.22158 16.7152 4.19651 16.4606 4.24537 16.215C4.29423 15.9693 4.41483 15.7437 4.59193 15.5666C4.76902 15.3895 4.99466 15.2689 5.2403 15.2201C5.48594 15.1712 5.74055 15.1963 5.97194 15.2921C6.20333 15.388 6.4011 15.5503 6.54024 15.7585C6.67939 15.9668 6.75365 16.2116 6.75365 16.462C6.75365 16.7979 6.62024 17.12 6.38276 17.3574C6.14528 17.5949 5.82319 17.7283 5.48734 17.7283ZM10.1305 17.7283C9.88003 17.7283 9.6352 17.6541 9.42696 17.5149C9.21871 17.3758 9.05641 17.178 8.96056 16.9466C8.86472 16.7152 8.83964 16.4606 8.8885 16.215C8.93736 15.9693 9.05797 15.7437 9.23507 15.5666C9.41216 15.3895 9.6378 15.2689 9.88344 15.2201C10.1291 15.1712 10.3837 15.1963 10.6151 15.2921C10.8465 15.388 11.0442 15.5503 11.1834 15.7585C11.3225 15.9668 11.3968 16.2116 11.3968 16.462C11.3968 16.7979 11.2634 17.12 11.0259 17.3574C10.7884 17.5949 10.4663 17.7283 10.1305 17.7283ZM10.1305 13.5073C9.88003 13.5073 9.6352 13.433 9.42696 13.2939C9.21871 13.1548 9.05641 12.957 8.96056 12.7256C8.86472 12.4942 8.83964 12.2396 8.8885 11.994C8.93736 11.7483 9.05797 11.5227 9.23507 11.3456C9.41216 11.1685 9.6378 11.0479 9.88344 10.999C10.1291 10.9502 10.3837 10.9752 10.6151 11.0711C10.8465 11.1669 11.0442 11.3292 11.1834 11.5375C11.3225 11.7457 11.3968 11.9905 11.3968 12.241C11.3968 12.5768 11.2634 12.8989 11.0259 13.1364C10.7884 13.3739 10.4663 13.5073 10.1305 13.5073ZM14.7736 17.7283C14.5232 17.7283 14.2783 17.6541 14.0701 17.5149C13.8619 17.3758 13.6995 17.178 13.6037 16.9466C13.5079 16.7152 13.4828 16.4606 13.5316 16.215C13.5805 15.9693 13.7011 15.7437 13.8782 15.5666C14.0553 15.3895 14.2809 15.2689 14.5266 15.2201C14.7722 15.1712 15.0268 15.1963 15.2582 15.2921C15.4896 15.388 15.6874 15.5503 15.8265 15.7585C15.9657 15.9668 16.0399 16.2116 16.0399 16.462C16.0399 16.7979 15.9065 17.12 15.669 17.3574C15.4316 17.5949 15.1095 17.7283 14.7736 17.7283ZM14.7736 13.5073C14.5232 13.5073 14.2783 13.433 14.0701 13.2939C13.8619 13.1548 13.6995 12.957 13.6037 12.7256C13.5079 12.4942 13.4828 12.2396 13.5316 11.994C13.5805 11.7483 13.7011 11.5227 13.8782 11.3456C14.0553 11.1685 14.2809 11.0479 14.5266 10.999C14.7722 10.9502 15.0268 10.9752 15.2582 11.0711C15.4896 11.1669 15.6874 11.3292 15.8265 11.5375C15.9657 11.7457 16.0399 11.9905 16.0399 12.241C16.0399 12.5768 15.9065 12.8989 15.669 13.1364C15.4316 13.3739 15.1095 13.5073 14.7736 13.5073ZM18.5725 6.75365H1.68841V3.37683H4.22103V4.22103C4.22103 4.44493 4.30998 4.65966 4.4683 4.81798C4.62662 4.9763 4.84134 5.06524 5.06524 5.06524C5.28914 5.06524 5.50387 4.9763 5.66219 4.81798C5.8205 4.65966 5.90945 4.44493 5.90945 4.22103V3.37683H14.3515V4.22103C14.3515 4.44493 14.4405 4.65966 14.5988 4.81798C14.7571 4.9763 14.9718 5.06524 15.1957 5.06524C15.4196 5.06524 15.6343 4.9763 15.7927 4.81798C15.951 4.65966 16.0399 4.44493 16.0399 4.22103V3.37683H18.5725V6.75365Z"
                            fill="#293050"
                          />
                        </svg>
                      </div>
                    </div>
                  )}
                  plugins={
                    showTime
                      ? [<TimePicker key="time-picker1" position="bottom" />]
                      : []
                  }
                />
                {(errorMessage || inputErrorMessage) && (
                  <span className="p-1 text-tiny text-danger">
                    {errorMessage ?? inputErrorMessage}
                  </span>
                )}
              </div>
            );
          }
          case "range": {
            return (
              <DateRangePicker
                defaultValue={extractDateObjtostr()}
                onChange={onRangeDateChange}
                label={label}
                onKeyDown={e => e.preventDefault()}
                //maxValue={today(getLocalTimeZone())}
                minValue={minValue}
                maxValue={maxValue}
                variant="bordered"
              />
            );
          }
          case "icon": {
            return (
              <div>
                <DatePicker
                  {...props}
                  value={selectedRangeDates}
                  range={true}
                  ref={datePickerRef}
                  format={showTime ? "MM-DD-YYYY HH:mm:ss" : "MM-DD-YYYY"}
                  onChange={onDateChange}
                  render={<Icon className="text-neo-gray" />}
                  //minDate={minValue}
                  //    minDate={minValue}
                  maxDate={!allowFuture ? new Date() : undefined}
                  plugins={
                    showTime
                      ? [
                          <TimePicker key="time-picker2" position="bottom" />,
                          <DatePanel key="time-picker3" markFocused />
                        ]
                      : []
                  }
                >
                  <div className="flex">
                    <p className="mb-1 ml-5 text-xs text-blue-800">
                      * Future dates and times are not allowed.
                    </p>
                  </div>
                  <div className="mx-2 flex justify-end gap-2">
                    <NeoButton
                      className=" mb-2"
                      type="submit"
                      color="black"
                      onClick={handleApplyClick}
                      size="sm"
                    >
                      Apply
                    </NeoButton>
                    <NeoButton
                      className="mb-2"
                      type="submit"
                      color="white"
                      onClick={handleApplyClick}
                      size="sm"
                    >
                      Close
                    </NeoButton>
                  </div>
                </DatePicker>
              </div>
            );
          }
          default: {
            return <span className="text-neo-red">Error</span>;
          }
        }
      })()}
    </>
  );
};
